import type { UIMessage } from "ai";
import { z } from "zod";

const visualUniverseRegenerationPreviewSchema = z.object({
  render: z.object({ component: z.literal("visual_universe_regeneration_confirmation") }),
  summary: z.string().trim().min(1).max(600).nullable().optional(),
  sourceGeneratedAssetId: z.uuid(),
  signedUrl: z.url(),
  direction: z.string().trim().min(3).max(2_000).nullable().optional(),
});

export type AssistantVisualUniverseRegenerationPreviewReference = {
  id: string;
  summary: string | null;
  sourceGeneratedAssetId: string;
  signedUrl: string;
  direction: string | null;
};

export function getAssistantVisualUniverseRegenerationPreviewReferences(
  message: Pick<UIMessage, "parts">,
): AssistantVisualUniverseRegenerationPreviewReference[] {
  const references: AssistantVisualUniverseRegenerationPreviewReference[] = [];

  for (const [index, part] of message.parts.entries()) {
    const candidate = part as {
      type?: string;
      state?: string;
      toolCallId?: string;
      output?: unknown;
    };

    if (
      candidate.state !== "output-available" ||
      candidate.type !== "tool-preview_visual_universe_regeneration"
    ) {
      continue;
    }

    const reference = parseAssistantVisualUniverseRegenerationPreviewReference(
      candidate.output,
      typeof candidate.toolCallId === "string"
        ? candidate.toolCallId
        : `visual-universe-regeneration-${index}`,
    );
    if (reference) {
      references.push(reference);
    }
  }

  return references;
}

export function isAssistantVisualUniverseRegenerationProposalStale(
  messages: readonly Pick<UIMessage, "id" | "role">[],
  proposalMessageId: string,
) {
  const proposalMessageIndex = messages.findIndex((message) => message.id === proposalMessageId);
  if (proposalMessageIndex === -1) {
    return false;
  }

  return messages.slice(proposalMessageIndex + 1).some((message) => message.role === "user");
}

function parseAssistantVisualUniverseRegenerationPreviewReference(
  value: unknown,
  id: string,
): AssistantVisualUniverseRegenerationPreviewReference | null {
  const parsed = visualUniverseRegenerationPreviewSchema.safeParse(value);
  if (!parsed.success) {
    return null;
  }

  return {
    id,
    summary: parsed.data.summary?.trim() || null,
    sourceGeneratedAssetId: parsed.data.sourceGeneratedAssetId,
    signedUrl: parsed.data.signedUrl,
    direction: parsed.data.direction?.trim() || null,
  };
}
